import { useState, useCallback, useEffect } from "react";
import { useGlobalAPI } from "../../../../hooks/useGlobalAPI";
import { useOcelMetadata } from "../../../../stores/OcelContext";
import { useGlobalFilters } from "../../../../stores/GlobalFilterContext/GlobalFilterContext";

const useTotalDowntimeKPI = (activeFile) => {
  const api = useGlobalAPI(activeFile);
  const metadata = useOcelMetadata(activeFile);
  const { filterState } = useGlobalFilters();

  // Data and loading states
  const [chartData, setChartData] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Metadata states
  const [objectTypes, setObjectTypes] = useState([]);
  const [objectTypeActivities, setObjectTypeActivities] = useState({});
  const [availableResources, setAvailableResources] = useState([]);
  const [isMetadataLoaded, setIsMetadataLoaded] = useState(false);

  // Configuration states
  const [title, setTitle] = useState("Total Downtime");
  const [selectedObjectType, setSelectedObjectType] = useState("");
  const [cycleStartActivity, setCycleStartActivity] = useState("cycle_start");
  const [cycleEndActivity, setCycleEndActivity] = useState("cycle_end");
  const [selectedResources, setSelectedResources] = useState([]);
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
  const [shiftStart, setShiftStart] = useState("06:00");
  const [shiftEnd, setShiftEnd] = useState("14:00");
  const [breaks, setBreaks] = useState([]);
  const [toleranceThreshold, setToleranceThreshold] = useState(60);
  const [ignoreShortTransitions, setIgnoreShortTransitions] = useState(false);
  const [includeNonWorkingDays, setIncludeNonWorkingDays] = useState(false);
  const [includeNoDataResources, setIncludeNoDataResources] = useState(false);
  const [multiResourceMode, setMultiResourceMode] = useState("accumulated");
  const [resourceColumn, setResourceColumn] = useState("ressource");

  // Reactively sync all dropdown data from OcelContext metadata whenever it
  // becomes available or changes (file switch). No API calls needed for unfiltered data.
  useEffect(() => {
    if (!metadata?.object_types) return;
    const hasActiveFilters = Object.values(filterState).some((v) =>
      Array.isArray(v) ? v.length > 0 : v?.enabled === true,
    );
    if (hasActiveFilters) return;

    const types = Object.keys(metadata.object_types);
    setObjectTypes(types);

    const activities = metadata.event_types || [];
    const activitiesMap = types.reduce((acc, t) => {
      acc[t] = activities;
      return acc;
    }, {});
    setObjectTypeActivities(activitiesMap);

    if (metadata.resource_values) {
      setAvailableResources(metadata.resource_values);
    }
    if (metadata.resource_column) {
      setResourceColumn(metadata.resource_column);
    }
    setIsMetadataLoaded(true);
  }, [metadata, filterState, activeFile]);

  // Auto-select first object type once types are loaded and nothing is selected
  useEffect(() => {
    if (!selectedObjectType && objectTypes.length > 0) {
      setSelectedObjectType(objectTypes[0]);
    }
  }, [objectTypes, selectedObjectType]);

  // Break helpers
  const addBreak = useCallback(() => {
    setBreaks((prev) => [...prev, { start: "12:00", end: "12:30" }]);
  }, []);

  const removeBreak = useCallback((idx) => {
    setBreaks((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const updateBreak = useCallback((idx, field, value) => {
    setBreaks((prev) =>
      prev.map((b, i) => (i === idx ? { ...b, [field]: value } : b)),
    );
  }, []);

  // Fetch metadata (object types, activities, and available resources)
  // Called when the settings modal opens so the dropdowns are always fresh.
  const fetchMetadata = useCallback(async () => {
    if (!activeFile) {
      setError("No active file selected");
      return false;
    }

    setLoading(true);
    setError(null);

    const params = { file_type: activeFile };

    // Detect whether any global filter is currently active
    const hasActiveFilters = Object.values(filterState).some((v) =>
      Array.isArray(v) ? v.length > 0 : v?.enabled === true,
    );

    try {
      if (hasActiveFilters) {
        // Filters active — fetch filtered object types, activities, and resources from API.
        const [objectTypesResponse, activitiesResponse, resourceResponse] =
          await Promise.all([
            api.get("/object-types", params, {}, "Object Types"),
            api.get("/object-type-activities", params, {}, "Object Type Activities"),
            api.get("/ocel/resource-values", params, {}, "Resource Values"),
          ]);
        setObjectTypes(objectTypesResponse.objectTypes || []);
        setObjectTypeActivities(activitiesResponse.objectTypeActivities || {});
        setAvailableResources(resourceResponse.values || []);
        if (resourceResponse.used_column) {
          setResourceColumn(resourceResponse.used_column.replace(/^ocel:/, ""));
        }
      }
      // No active filters: all data already synced from OcelContext metadata
      // by the reactive useEffect — zero API calls needed.

      setIsMetadataLoaded(true);
      setError(null);
      return true;
    } catch (err) {
      console.error("Error fetching metadata:", err);
      setError(err.message || "Failed to load metadata");
      setIsMetadataLoaded(false);
      return false;
    } finally {
      setLoading(false);
    }
  }, [activeFile, api, filterState]);

  // Fetch downtime data
  // Accepts an optional `overrides` object to use values that haven't been
  // committed to state yet (e.g. when called immediately after Apply).
  const fetchDowntimeData = useCallback(async (overrides = {}) => {
    const resources = overrides.selectedResources ?? selectedResources;
    const objectType = overrides.selectedObjectType ?? selectedObjectType;

    if (!activeFile || resources.length === 0) {
      setError("Please select at least one resource");
      return false;
    }
    if (!objectType) {
      setError("Please select an object type");
      return false;
    }

    // Latest-request guard to avoid stale updates
    const reqIdRef =
      fetchDowntimeData._reqIdRef ||
      (fetchDowntimeData._reqIdRef = { current: 0 });
    reqIdRef.current += 1;
    const myId = reqIdRef.current;

    const markLoading = () => {
      if (myId === reqIdRef.current) setLoading(true);
      setError(null);
    };
    const finishLoading = () => {
      if (myId === reqIdRef.current) setLoading(false);
    };

    markLoading();

    // Convert HH:mm to HH:mm:ss — backend uses only the time portion per day
    const toTimestamp = (hhmm) => `${hhmm}:00`;

    const resolvedShiftStart = overrides.shiftStart ?? shiftStart;
    const resolvedShiftEnd = overrides.shiftEnd ?? shiftEnd;
    const resolvedBreaks = overrides.breaks ?? breaks;
    const resolvedDateRange = overrides.dateRange ?? dateRange;

    const requestData = {
      file_type: activeFile,
      resources,
      shift: {
        start_time: toTimestamp(resolvedShiftStart),
        end_time: toTimestamp(resolvedShiftEnd),
      },
      breaks: resolvedBreaks.map((b) => ({
        start_time: toTimestamp(b.start),
        end_time: toTimestamp(b.end),
      })),
      tolerance_threshold_seconds: overrides.toleranceThreshold ?? toleranceThreshold,
      ignore_short_transitions: overrides.ignoreShortTransitions ?? ignoreShortTransitions,
      include_non_working_days: overrides.includeNonWorkingDays ?? includeNonWorkingDays,
      include_no_data_resources: overrides.includeNoDataResources ?? includeNoDataResources,
      multi_resource_mode: overrides.multiResourceMode ?? multiResourceMode,
      cycle_start_column: overrides.cycleStartActivity ?? cycleStartActivity,
      cycle_end_column: overrides.cycleEndActivity ?? cycleEndActivity,
      production_order_column: objectType,
      resource_column: overrides.resourceColumn ?? resourceColumn,
    };

    // Include date_range if user set dates
    // Append time components: startDate gets T00:00:00 (start of day),
    // endDate gets T23:59:59 (end of day) to include the full day
    if (resolvedDateRange.startDate || resolvedDateRange.endDate) {
      requestData.date_range = {
        enabled: true,
        startDate: resolvedDateRange.startDate
          ? `${resolvedDateRange.startDate}T00:00:00`
          : null,
        endDate: resolvedDateRange.endDate ? `${resolvedDateRange.endDate}T23:59:59` : null,
      };
    }

    console.group("[TotalDowntimeKPI] Fetch request");
    console.log("file:", requestData.file_type);
    console.log("resources:", requestData.resources);
    console.log("shift:", requestData.shift.start_time, "→", requestData.shift.end_time);
    console.log("breaks:", requestData.breaks.length > 0 ? requestData.breaks : "(none)");
    console.log("date_range:", requestData.date_range ?? "(none — backend will use data extent)");
    console.log("tolerance_seconds:", requestData.tolerance_threshold_seconds);
    console.log("ignore_short_transitions:", requestData.ignore_short_transitions);
    console.log("include_non_working_days:", requestData.include_non_working_days);
    console.log("include_no_data_resources:", requestData.include_no_data_resources);
    console.log("multi_resource_mode:", requestData.multi_resource_mode);
    console.log("resource_column:", requestData.resource_column);
    console.log("production_order_column:", requestData.production_order_column);
    console.groupEnd();

    const tryPostEndpoints = async (endpoints, payload, label) => {
      const tried = [];
      for (const ep of endpoints) {
        try {
          const res = await api.post(ep, payload, {}, label);
          return { res, endpoint: ep };
        } catch (e) {
          tried.push({ endpoint: ep, message: e?.message || String(e) });
          if (e?.message && /not found|404/i.test(e.message)) continue;
          throw e;
        }
      }
      const details = tried
        .map((t) => `${t.endpoint} -> ${t.message}`)
        .join(" | ");
      throw new Error(`All total-downtime endpoints failed: ${details}`);
    };

    const attemptFetch = async (allowRetry = true) => {
      try {
        const { res: responseData } = await tryPostEndpoints(
          ["/total-downtime", "/visualization/total-downtime"],
          requestData,
          "Total Downtime",
        );

        if (myId !== reqIdRef.current) {
          return false;
        }

        if (responseData && "daily_downtime" in responseData) {
          const dataPoints = responseData.daily_downtime || [];
          setChartData(dataPoints);
          setSummaryData({
            total_downtime_hours: responseData.total_downtime_hours,
            total_downtime_minutes: responseData.total_downtime_minutes,
            avg_daily_downtime_minutes: responseData.avg_daily_downtime_minutes,
            avg_daily_downtime_hours: responseData.avg_daily_downtime_hours,
            overall_downtime_percentage:
              responseData.overall_downtime_percentage,
            days_analyzed: responseData.days_analyzed,
            reference_transition_times: responseData.reference_transition_times,
            resource_summaries: responseData.resource_summaries,
            multi_resource_mode: responseData.multi_resource_mode,
            start_date: responseData.start_date,
            end_date: responseData.end_date,
          });
          console.group("[TotalDowntimeKPI] Response");
          console.log(`days_analyzed: ${responseData.days_analyzed}, data_points: ${dataPoints.length}`);
          console.log(`date_range: ${responseData.start_date} → ${responseData.end_date}`);
          console.log(`total_downtime: ${responseData.total_downtime_hours}h (${responseData.total_downtime_minutes}min)`);
          console.log(`avg_daily: ${responseData.avg_daily_downtime_hours}h/day`);
          console.log(`overall_downtime_pct: ${responseData.overall_downtime_percentage}%`);
          if (responseData.resource_summaries) {
            console.log("resource_summaries:", responseData.resource_summaries);
          }
          if (responseData.reference_transition_times) {
            console.log("reference_transition_times:", responseData.reference_transition_times);
          }
          console.table(dataPoints.map(d => ({
            date: d.date,
            shift_h: +(d.shift_duration_seconds / 3600).toFixed(3),
            productive_h: +(d.productive_time_seconds / 3600).toFixed(3),
            downtime_h: +(d.downtime_hours ?? d.downtime_seconds / 3600).toFixed(3),
            downtime_pct: d.downtime_percentage,
            no_data: d.no_data ?? false,
          })));
          console.groupEnd();
          finishLoading();
          return true;
        } else {
          console.error("Invalid response format:", responseData);
          setChartData([]);
          setSummaryData(null);
          setError("Server returned invalid response format");
          finishLoading();
          return false;
        }
      } catch (err) {
        console.error("Error fetching total downtime data:", err);

        const msg = err?.message || "";
        const is500 =
          /\b(500|Server error)\b/i.test(msg) ||
          /processing request/i.test(msg);
        if (allowRetry && is500) {
          console.warn("Retrying once after transient server error...");
          await new Promise((r) => setTimeout(r, 300));
          return await attemptFetch(false);
        }

        if (myId === reqIdRef.current) {
          let errorMessage = "Failed to load downtime data";
          if (msg.includes("404")) {
            errorMessage =
              "Data endpoint not found. Please check if the backend is running.";
          } else if (msg.includes("500") || /Server error/i.test(msg)) {
            errorMessage =
              "Server error processing request. Please check the backend logs.";
          } else if (msg.includes("400")) {
            errorMessage =
              "Invalid configuration parameters. Please check your selections.";
          } else if (msg) {
            errorMessage = `Server error: ${msg}`;
          }
          setError(errorMessage);
          setChartData([]);
          setSummaryData(null);
          finishLoading();
        }
        return false;
      }
    };

    return await attemptFetch(true);
  }, [
    activeFile,
    selectedResources,
    dateRange,
    shiftStart,
    shiftEnd,
    breaks,
    toleranceThreshold,
    ignoreShortTransitions,
    includeNonWorkingDays,
    includeNoDataResources,
    multiResourceMode,
    cycleStartActivity,
    cycleEndActivity,
    selectedObjectType,
    resourceColumn,
    api,
  ]);

  // Clear error when user changes configuration inputs
  useEffect(() => {
    if (error) {
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedResources,
    dateRange,
    shiftStart,
    shiftEnd,
    breaks,
    toleranceThreshold,
    ignoreShortTransitions,
    includeNonWorkingDays,
    includeNoDataResources,
    multiResourceMode,
  ]);

  // Reset data and user selections when activeFile changes.
  // Metadata states are NOT cleared here — the metadata sync effect
  // (deps: [metadata, filterState, activeFile]) will re-run and repopulate them.
  useEffect(() => {
    setChartData([]);
    setSummaryData(null);
    setError(null);
    setSelectedResources([]);
    setSelectedObjectType("");
    setCycleStartActivity("cycle_start");
    setCycleEndActivity("cycle_end");
    setResourceColumn("ressource");
    setDateRange({ startDate: "", endDate: "" });
    setBreaks([]);
    setIncludeNonWorkingDays(false);
    setIncludeNoDataResources(false);
  }, [activeFile]);

  return {
    // Data
    chartData,
    summaryData,
    loading,
    error,

    // Metadata
    objectTypes,
    objectTypeActivities,
    availableResources,
    isMetadataLoaded,

    // Configuration state
    title,
    selectedObjectType,
    cycleStartActivity,
    cycleEndActivity,
    selectedResources,
    dateRange,
    shiftStart,
    shiftEnd,
    breaks,
    toleranceThreshold,
    ignoreShortTransitions,
    includeNonWorkingDays,
    includeNoDataResources,
    multiResourceMode,
    resourceColumn,

    // Setters
    setTitle,
    setSelectedObjectType,
    setCycleStartActivity,
    setCycleEndActivity,
    setSelectedResources,
    setDateRange,
    setShiftStart,
    setShiftEnd,
    setBreaks,
    setToleranceThreshold,
    setIgnoreShortTransitions,
    setIncludeNonWorkingDays,
    setIncludeNoDataResources,
    setMultiResourceMode,

    // Helpers
    addBreak,
    removeBreak,
    updateBreak,

    // Actions
    fetchMetadata,
    fetchDowntimeData,
  };
};

export { useTotalDowntimeKPI };
